import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Search, FileText } from 'lucide-react';
import { Input } from '../ui/input';
import { getAllPages } from '../../docs';

const DocsSearch = () => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const searchRef = useRef(null);
  const navigate = useNavigate();

  // Close search results when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Simple client-side search
  const handleSearch = (searchQuery) => {
    setQuery(searchQuery);
    
    if (searchQuery.length < 2) {
      setResults([]);
      setIsOpen(false);
      return;
    }

    const allPages = getAllPages();
    const searchLower = searchQuery.toLowerCase();
    
    const filtered = allPages.filter(page => {
      const titleMatch = page.title.toLowerCase().includes(searchLower);
      const descMatch = page.description?.toLowerCase().includes(searchLower);
      const sectionMatch = page.sectionTitle.toLowerCase().includes(searchLower);
      
      return titleMatch || descMatch || sectionMatch;
    });

    setResults(filtered.slice(0, 8)); // Limit to 8 results
    setIsOpen(filtered.length > 0);
  };

  const handleResultClick = (path) => {
    setQuery('');
    setResults([]);
    setIsOpen(false);
    navigate(path);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      setIsOpen(false);
    }
  };

  return (
    <div className="mb-6 relative" ref={searchRef}>
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-foreground/40" />
        <Input
          placeholder="Search documentation..."
          value={query}
          onChange={(e) => handleSearch(e.target.value)}
          onFocus={() => query.length >= 2 && results.length > 0 && setIsOpen(true)}
          onKeyDown={handleKeyDown}
          className="pl-10 bg-card/50 border-border/40 focus:border-purple-500/50"
        />
      </div>
      
      {isOpen && results.length > 0 && (
        <div className="absolute top-full left-0 right-0 mt-2 bg-card border border-border/40 rounded-lg shadow-xl z-50 max-h-96 overflow-y-auto">
          {results.map((result, index) => (
            <button
              key={`${result.section}-${result.slug}`}
              onClick={() => handleResultClick(result.path)}
              className="w-full text-left px-4 py-3 hover:bg-purple-500/10 border-b border-border/40 last:border-0 transition-colors"
            >
              <div className="flex items-start gap-3">
                <FileText className="h-4 w-4 text-purple-400 mt-1 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-foreground">{result.title}</div>
                  <div className="text-xs text-purple-400/70 mt-0.5">
                    {result.sectionTitle}
                  </div>
                  {result.description && (
                    <div className="text-sm text-foreground/60 mt-1 line-clamp-2">
                      {result.description}
                    </div>
                  )}
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default DocsSearch;

